import { cn } from "@/lib/utils";
import type { Severity, SeverityCounts, ScanStatus, VulnerabilityStatus } from "@/lib/api/types";

type SevMeta = { label: string; short: string; fg: string; soft: string; bar: string };

export const SEVERITY_META: Record<Severity, SevMeta> = {
  critical:      { label: "Critical",      short: "C", fg: "text-sev-critical", soft: "bg-sev-critical-soft", bar: "bg-sev-critical" },
  high:          { label: "High",          short: "H", fg: "text-sev-high",     soft: "bg-sev-high-soft",     bar: "bg-sev-high" },
  medium:        { label: "Medium",        short: "M", fg: "text-sev-medium",   soft: "bg-sev-medium-soft",   bar: "bg-sev-medium" },
  low:           { label: "Low",           short: "L", fg: "text-sev-low",      soft: "bg-sev-low-soft",      bar: "bg-sev-low" },
  informational: { label: "Informational", short: "I", fg: "text-sev-info",     soft: "bg-sev-info-soft",     bar: "bg-sev-info" },
};

const ORDER: Severity[] = ["critical", "high", "medium", "low", "informational"];

/** Solid severity pill with a leading marker. */
export function SeverityBadge({ severity, className }: { severity: Severity; className?: string }) {
  const m = SEVERITY_META[severity];
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold", m.soft, m.fg, className)}>
      <span className={cn("size-1.5 rounded-full", m.bar)} />
      {m.label}
    </span>
  );
}

/** Compact per-severity count cells (the dense breakdown used in table rows). */
export function SeverityCountCells({ counts }: { counts: SeverityCounts }) {
  const cells: { sev: Severity; n: number }[] = [
    { sev: "critical", n: counts.critical },
    { sev: "high", n: counts.high },
    { sev: "medium", n: counts.medium },
    { sev: "low", n: counts.low },
    { sev: "informational", n: counts.informational ?? 0 },
  ];
  return (
    <div className="flex items-center gap-1">
      {cells.map(({ sev, n }) => {
        const m = SEVERITY_META[sev];
        return (
          <span key={sev} title={`${m.label}: ${n}`}
            className={cn("inline-flex min-w-[26px] items-center justify-center rounded px-1 py-0.5 font-mono text-xs font-semibold tabular-nums",
              n > 0 ? cn(m.soft, m.fg) : "bg-muted/60 text-muted-foreground/40")}>
            {/* NFR-32 (WCAG §1.3.1): the colour and the tooltip are not a name —
                a screen reader hears "High: 2", not a bare "2". */}
            <span className="sr-only">{m.label}: </span>
            {n}
          </span>
        );
      })}
    </div>
  );
}

/**
 * THE SIGNATURE — the severity spine.
 * A single segmented meter that encodes the true severity distribution. Segment
 * widths are proportional to counts; zero-count severities collapse to nothing.
 */
export function SeveritySpine({
  counts, className, height = "h-2", showZeroTrack = true,
}: { counts: SeverityCounts; className?: string; height?: string; showZeroTrack?: boolean }) {
  const cells = ORDER.map((sev) => ({ sev, n: sev === "informational" ? counts.informational ?? 0 : counts[sev] }));
  const total = cells.reduce((a, c) => a + c.n, 0);

  if (total === 0) {
    return showZeroTrack ? <div className={cn("w-full rounded-full bg-muted", height, className)} /> : null;
  }
  return (
    <div className={cn("flex w-full overflow-hidden rounded-full", height, className)}>
      {cells.filter((c) => c.n > 0).map((c) => (
        <span key={c.sev} title={`${SEVERITY_META[c.sev].label}: ${c.n}`}
          className={cn(SEVERITY_META[c.sev].bar, "h-full")}
          style={{ width: `${(c.n / total) * 100}%` }} />
      ))}
    </div>
  );
}

/** Legend row for the spine — short marker + label + count. */
export function SeverityLegend({ counts, className }: { counts: SeverityCounts; className?: string }) {
  return (
    <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1", className)}>
      {ORDER.map((sev) => {
        const n = sev === "informational" ? counts.informational ?? 0 : counts[sev];
        const m = SEVERITY_META[sev];
        return (
          <span key={sev} className="inline-flex items-center gap-1.5 text-xs">
            <span className={cn("size-2 rounded-[3px]", m.bar)} />
            <span className="text-muted-foreground">{m.label}</span>
            <span className="font-mono font-semibold tabular-nums">{n}</span>
          </span>
        );
      })}
    </div>
  );
}

/* ── Status badges ──────────────────────────────────────────────────────── */

/**
 * One entry per state in SDD Appendix A, and the labels say what the state
 * means rather than restating its identifier.
 *
 * `completed_partial` reads "Completed (partial)" and carries a WARNING colour,
 * not the success colour `completed` gets. FR-20a and FR-20b put a run in that
 * state when evidence was lost or an engine did not run, so it is the one
 * conclusion an operator most needs to notice; rendering it in green beside the
 * clean runs would bury exactly the thing the state exists to report.
 */
const SCAN_STATUS_META: Record<ScanStatus, { label: string; soft: string; fg: string; pulse?: boolean }> = {
  scheduled:         { label: "Scheduled",           soft: "bg-primary/10", fg: "text-primary" },
  queued:            { label: "Queued",              soft: "bg-muted", fg: "text-muted-foreground" },
  running:           { label: "Running",             soft: "bg-sev-low-soft", fg: "text-sev-low", pulse: true },
  paused:            { label: "Paused",              soft: "bg-muted", fg: "text-muted-foreground" },
  aborted:           { label: "Aborted",             soft: "bg-sev-high-soft", fg: "text-sev-high" },
  completed:         { label: "Completed",           soft: "bg-ok-soft", fg: "text-ok" },
  failed:            { label: "Failed",              soft: "bg-sev-critical-soft", fg: "text-sev-critical" },
  completed_partial: { label: "Completed (partial)", soft: "bg-sev-medium-soft", fg: "text-sev-medium" },
};

/** The console's name for a run state — the same word its badge shows. */
export function scanStatusLabel(status: ScanStatus): string {
  return SCAN_STATUS_META[status].label;
}

export function ScanStatusBadge({ status }: { status: ScanStatus }) {
  const m = SCAN_STATUS_META[status];
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium", m.soft, m.fg)}>
      <span className={cn("size-1.5 rounded-full bg-current", m.pulse && "animate-pulse")} />
      {m.label}
    </span>
  );
}

const VULN_STATUS_META: Record<VulnerabilityStatus, { label: string; soft: string; fg: string }> = {
  open:           { label: "Open",           soft: "bg-sev-low-soft", fg: "text-sev-low" },
  fixed:          { label: "Fixed",          soft: "bg-ok-soft", fg: "text-ok" },
  ignored:        { label: "Ignored",        soft: "bg-muted", fg: "text-muted-foreground" },
  false_positive: { label: "False Positive", soft: "bg-muted", fg: "text-muted-foreground" },
  accepted_risk:  { label: "Accepted Risk",  soft: "bg-sev-medium-soft", fg: "text-sev-medium" },
};

export function VulnStatusBadge({ status }: { status: VulnerabilityStatus }) {
  const m = VULN_STATUS_META[status];
  return <span className={cn("inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium", m.soft, m.fg)}>{m.label}</span>;
}
